// "Email this form" box on MSQA forms (review #29, Connor #723, 27 Oct 2026).
// Backend: src/wcc_viewer/msqa_email.py (+ mailer.py for "Send from WRAP").
//
// Used by the field app's form editor (a DRAFT: the recipients live on the
// phone's copy of the form and ride along on its queued autosave, never in
// the hashed content), the field app's Submitted list, and the desktop review
// panel (a SUBMITTED form: the list is saved with PUT .../recipients).
//
//   * suggestions ticked by default: PM, S&P lead, stakeholders with an
//     email, monitors on the project, My projects people, the PM's "always
//     send MSQA forms to" list; plus typed addresses and, on Android Chrome,
//     "Pick from contacts" (Contact Picker API, feature-detected);
//   * after submit: "Open in my mail app" (mailto with a short summary + a
//     no-login link to the sealed PDF), "Share PDF" (Web Share API with the
//     file, else a download), and "Send from WRAP" only when the server says
//     WRAP sending is on. Every attempt goes on the form's audit trail.
import { getJson, postJson } from "../api";
import { showToast } from "../toast";
import { fill, h, fmtDate } from "./dom";
import { enqueue, newUuid } from "./outbox";

export interface Recipient {
  email: string;
  name?: string | null;
}
interface Suggestion extends Recipient {
  sources: string[];
  default: boolean;
}
interface WrapSending {
  enabled: boolean;
  max_attachment_mb?: number;
}
interface Send {
  at: string;
  who: string | null;
  how: string;
  n_recipients: number;
  status?: string | null;
}
interface BoxState {
  suggestions: Suggestion[];
  saved: Recipient[] | null;
  selected: Recipient[];
  can_email: boolean;
  sends: Send[];
  wrap_sending: WrapSending;
}

const SOURCE: Record<string, string> = {
  pm: "PM", sp_lead: "S&P lead", stakeholder: "stakeholder", monitor: "monitor", my_projects: "on the project",
  always: "always sent", you: "you",
};
const HOW: Record<string, string> = { mail_app: "mail app", share: "shared PDF", wrap: "sent from WRAP" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUGG_CACHE = "wrap.msqa.emailSuggestions.";

interface ContactsManager {
  select(props: string[], opts?: { multiple?: boolean }): Promise<{ name?: string[]; email?: string[] }[]>;
}
function contactPicker(): ContactsManager | null {
  const nav = navigator as Navigator & { contacts?: ContactsManager };
  return "contacts" in navigator && "ContactsManager" in window && nav.contacts ? nav.contacts : null;
}

function err(e: unknown): string {
  const d = (e as { detail?: unknown }).detail;
  return typeof d === "string" ? d : (e as Error).message;
}

async function putJson<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, { method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.detail ?? r.statusText), { detail: j.detail });
  return j as T;
}

function cachedSuggestions(projectId: string): { suggestions: Suggestion[]; wrap_sending: WrapSending } | null {
  try {
    const s = localStorage.getItem(SUGG_CACHE + projectId);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

export interface EmailBoxOpts {
  formId: string;
  projectId: string;
  /** draft = recipients held by the caller (the phone's form) and passed to onChange; submitted = saved on the server */
  mode: "draft" | "submitted";
  /** draft mode: the phone's current list (undefined = nobody chose yet: the suggestions' defaults apply) */
  recipients?: Recipient[];
  onChange?: (list: Recipient[]) => void;
  /** start open (e.g. straight after submit) */
  open?: boolean;
  title?: string;
}

/** The box. Returns at once; fills itself when the server answers (or from the phone's cache offline). */
export function emailBox(o: EmailBoxOpts): HTMLElement {
  const box = h("details", { class: "msqa-email", open: o.open ?? false });
  const summary = h("summary", {}, o.title ?? "Email this form");
  const body = h("div", { class: "msqa-email-body" });
  box.append(summary, body);
  let st: BoxState | null = null;
  let chosen: Recipient[] = [];
  const extra: Recipient[] = []; // typed / picked addresses not among the suggestions
  let saveTimer: number | null = null;

  const setChosen = (list: Recipient[], persist = true) => {
    chosen = list;
    summary.textContent = `${o.title ?? "Email this form"} · ${chosen.length} recipient${chosen.length === 1 ? "" : "s"}`;
    if (!persist) return;
    if (o.mode === "draft") o.onChange?.(chosen);
    else {
      if (saveTimer) window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => {
        void putJson<BoxState>(`/api/msqa/forms/${encodeURIComponent(o.formId)}/recipients`, { recipients: chosen })
          .catch((e) => showToast(`Recipients not saved: ${err(e)}`));
      }, 600);
    }
  };

  const draw = () => {
    if (!st) return;
    const suggested = new Set(st.suggestions.map((s) => s.email));
    for (const r of chosen) if (!suggested.has(r.email) && !extra.some((x) => x.email === r.email)) extra.push(r);
    const isOn = (e: string) => chosen.some((r) => r.email === e);
    const toggle = (r: Recipient, on: boolean) => {
      setChosen(on ? [...chosen.filter((x) => x.email !== r.email), { email: r.email, name: r.name ?? null }] : chosen.filter((x) => x.email !== r.email));
    };
    const row = (r: Recipient, note: string) => {
      const cb = h("input", { type: "checkbox", checked: isOn(r.email) }) as HTMLInputElement;
      cb.addEventListener("change", () => toggle(r, cb.checked));
      return h("label", { class: "msqa-email-row" }, cb,
        h("span", {}, h("strong", {}, r.name || r.email), r.name ? h("small", {}, ` ${r.email}`) : null, note ? h("small", { class: "fa-muted" }, ` · ${note}`) : null));
    };
    const input = h("input", { type: "email", class: "fa-input", placeholder: "name@example.com", autocomplete: "email", inputmode: "email" }) as HTMLInputElement;
    const add = () => {
      const v = input.value.trim().toLowerCase();
      if (!EMAIL_RE.test(v)) {
        showToast("That doesn't look like an email address");
        return;
      }
      if (!suggested.has(v) && !extra.some((x) => x.email === v)) extra.push({ email: v });
      input.value = "";
      toggle({ email: v }, true);
      draw();
    };
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); add(); } });
    const picker = contactPicker();
    const pick = picker ? h("button", { type: "button", class: "fa-btn", onclick: () => {
      picker.select(["name", "email"], { multiple: true }).then((got) => {
        let n = 0;
        for (const c of got) {
          const e = (c.email ?? [])[0]?.trim().toLowerCase();
          if (!e || !EMAIL_RE.test(e)) continue;
          if (!suggested.has(e) && !extra.some((x) => x.email === e)) extra.push({ email: e, name: c.name?.[0] ?? null });
          if (!isOn(e)) chosen = [...chosen, { email: e, name: c.name?.[0] ?? null }];
          n++;
        }
        setChosen(chosen);
        draw();
        if (!n && got.length) showToast("Those contacts have no email address");
      }).catch(() => { /* picker closed */ });
    } }, "Pick from contacts") : null;
    const actions = o.mode === "submitted" ? afterSubmit() : null;
    fill(body,
      st.suggestions.length ? h("div", { class: "msqa-email-list" }, ...st.suggestions.map((s) => row(s, s.sources.map((x) => SOURCE[x] ?? x).join(", ")))) : h("p", { class: "fa-muted" }, "WRAP knows nobody with an email on this project yet - type an address."),
      extra.length ? h("div", { class: "msqa-email-list" }, ...extra.map((r) => row(r, "added"))) : null,
      h("div", { class: "msqa-email-add" }, input, h("button", { type: "button", class: "fa-btn", onclick: add }, "Add"), pick),
      o.mode === "draft" ? h("p", { class: "fa-muted" }, "Saved with the draft; you can email it after submitting. Recipients aren't part of the signed form.") : null,
      actions,
      st.sends.length ? h("ul", { class: "msqa-email-sends fa-muted" }, ...st.sends.slice(0, 8).map((s) =>
        h("li", {}, `${fmtDate(s.at)} · ${s.who ?? "?"} · ${HOW[s.how] ?? s.how} · ${s.n_recipients} recipient${s.n_recipients === 1 ? "" : "s"}${s.status && s.status !== "sent" ? ` (${s.status})` : ""}`))) : null,
    );
  };

  const reload = () => void getJson<BoxState>(`/api/msqa/forms/${encodeURIComponent(o.formId)}/email`).then((r) => { st = r; draw(); }).catch(() => {});

  function afterSubmit(): HTMLElement | null {
    if (!st) return null;
    if (!st.can_email) return h("p", { class: "fa-muted" }, "Only the monitor who did this form, or the project manager, can email it.");
    const base = `/api/msqa/forms/${encodeURIComponent(o.formId)}`;
    const mail = h("button", { type: "button", class: "fa-btn fa-primary", onclick: () => {
      if (!chosen.length && !confirm("No recipients ticked - open your mail app anyway?")) return;
      postJson<{ mailto: string; share_error: string | null }>(`${base}/email/mail-app`, { recipients: chosen })
        .then((r) => {
          if (r.share_error) showToast(`No PDF link in the email: ${r.share_error}`);
          window.location.href = r.mailto;
          window.setTimeout(reload, 1500);
        })
        .catch((e) => showToast(navigator.onLine ? err(e) : "Needs signal: the email carries a link to the sealed PDF."));
    } }, "Open in my mail app");
    const share = h("button", { type: "button", class: "fa-btn", onclick: () => void sharePdf(base, chosen).then(reload) }, "Share PDF");
    const wrap = st.wrap_sending.enabled ? h("button", { type: "button", class: "fa-btn", onclick: (e: Event) => {
      if (!chosen.length) {
        showToast("Tick at least one recipient");
        return;
      }
      const b = e.currentTarget as HTMLButtonElement;
      b.disabled = true;
      postJson<{ status: string; error?: string | null; attached: string | null }>(`${base}/email/send`, { recipients: chosen })
        .then((r) => showToast(r.status === "sent" ? `Sent to ${chosen.length} recipient${chosen.length === 1 ? "" : "s"}${r.attached ? " with the PDF attached" : " with a link to the PDF"}` : `Not sent: ${r.error ?? r.status}`))
        .catch((er) => showToast(err(er)))
        .finally(() => { b.disabled = false; reload(); });
    } }, "Send from WRAP") : null;
    return h("div", { class: "msqa-email-actions" }, mail, share, wrap,
      h("p", { class: "fa-muted" }, `The email says the project, date, outcome and number of non-compliances (not the answers), with a no-login link to the sealed PDF.${st.wrap_sending.enabled ? " Send from WRAP attaches the PDF." : ""}`));
  }

  // load
  const fromServer = () => getJson<BoxState>(`/api/msqa/forms/${encodeURIComponent(o.formId)}/email`);
  const fromProject = () => getJson<{ suggestions: Suggestion[]; wrap_sending: WrapSending }>(`/api/msqa/projects/${encodeURIComponent(o.projectId)}/email-suggestions`)
    .then((r) => {
      try { localStorage.setItem(SUGG_CACHE + o.projectId, JSON.stringify(r)); } catch { /* storage full / blocked */ }
      return r;
    });
  const start = (s: BoxState) => {
    st = s;
    const first = o.mode === "draft" ? (o.recipients ?? (s.saved ?? s.suggestions.filter((x) => x.default))) : s.selected;
    setChosen(first.map((r) => ({ email: r.email, name: r.name ?? null })), false);
    draw();
  };
  const asState = (p: { suggestions: Suggestion[]; wrap_sending: WrapSending }): BoxState =>
    ({ suggestions: p.suggestions, saved: null, selected: [], can_email: true, sends: [], wrap_sending: p.wrap_sending });
  body.append(h("p", { class: "fa-muted" }, "Loading who to send it to…"));
  if (o.mode === "submitted") {
    fromServer().then(start).catch((e) => fill(body, h("p", { class: "fa-muted" }, navigator.onLine ? err(e) : "Needs signal to email a submitted form.")));
  } else {
    // a draft may not have reached the server yet: use the project's list (cached for offline)
    fromServer().then(start).catch(() => fromProject().then((p) => start(asState(p))).catch(() => {
      const c = cachedSuggestions(o.projectId);
      start(asState(c ?? { suggestions: [], wrap_sending: { enabled: false } }));
    }));
  }
  return box;
}

/** "Share PDF": the Web Share API with the sealed PDF file, else a download. Recorded either way. */
async function sharePdf(base: string, chosen: Recipient[]): Promise<void> {
  let blob: Blob;
  try {
    const r = await fetch(`${base}/outputs/clean?download=true`, { credentials: "same-origin" });
    if (!r.ok) throw new Error(r.status === 404 ? "The PDF isn't ready yet" : r.statusText);
    blob = await r.blob();
  } catch (e) {
    showToast(navigator.onLine ? (e as Error).message : "Needs signal to fetch the PDF.");
    return;
  }
  const file = new File([blob], `msqa-${base.split("/").pop()}.pdf`, { type: "application/pdf" });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  let fallback: string | null = null;
  if (nav.canShare?.({ files: [file] }) && navigator.share) {
    try {
      await navigator.share({ files: [file], title: "MSQA form" });
    } catch (e) {
      if ((e as DOMException).name === "AbortError") return; // closed the share sheet: nothing sent
      fallback = "download";
    }
  } else fallback = "download";
  if (fallback) {
    const url = URL.createObjectURL(file);
    const a = h("a", { href: url, download: file.name });
    document.body.append(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  const rec = { how: "share", recipients: chosen, fallback };
  try {
    await postJson(`${base}/email/record`, rec);
  } catch {
    // offline: the record waits in the outbox like every other queued call
    void enqueue({ client_uuid: `msqa-share-${newUuid()}`, endpoint: `${base}/email/record`, method: "POST", json: rec,
                   meta: { form_kind: "msqa_form", label: "MSQA share record" }, photos: [], photo_headings: [] });
  }
}
